import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { TelegramUser } from "../types";
import { useCourseProgress } from "../hooks/useCourseProgress";
import { useCourse } from "../hooks/useCourses";
import { SEOHead } from "../components/SEOHead";
import { generateCourseSchema, generateBreadcrumbSchema, SITE_URL } from "../utils/seo";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock,
  BookOpen,
  PlayCircle,
  Sparkles,
  X,
  ArrowRight,
  Rocket,
  Lightbulb,
  Target,
  Zap,
  TrendingUp,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  MessageCircle,
  Map,
} from "lucide-react";
import { AITutorCoursePanel } from "../components/AITutorCoursePanel";
import type { AITutorDualMode } from "../components/AITutorDualModePanel";
import { ModuleListItem } from "../components/ModuleListItem";
import { useResize } from "../hooks/useResize";
import {
  AI_TUTOR_ASIDE_WIDTH_KEY,
  AI_TUTOR_MIN_WIDTH,
  AI_TUTOR_MAX_WIDTH,
  AI_TUTOR_DEFAULT_WIDTH,
  AI_TUTOR_DESKTOP_TOP,
} from "../constants/aiTutorLayout";
import { LAYERS } from "../constants/layers";

interface CoursePageProps {
  user: TelegramUser | null;
}

export const CoursePage = ({ user }: CoursePageProps) => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { course, loading: courseLoading, error: courseError } = useCourse(slug);
  const { progress, stats, setLessonStatus } = useCourseProgress(user, slug || "");
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [showAITutor, setShowAITutor] = useState(true); // Открыт по умолчанию на desktop
  const [tutorAsideMode, setTutorAsideMode] = useState<AITutorDualMode>("chat");
  const [hoveredModuleId, setHoveredModuleId] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);

  const [isLg, setIsLg] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  const [chatPanelMinWidth, setChatPanelMinWidth] = useState(436);
  const aiTutorInitialWidth = useState(() => {
    try {
      const w = localStorage.getItem(AI_TUTOR_ASIDE_WIDTH_KEY);
      if (w) {
        const n = parseInt(w, 10);
        if (n >= AI_TUTOR_MIN_WIDTH && n <= AI_TUTOR_MAX_WIDTH) return n;
      }
    } catch {}
    return AI_TUTOR_DEFAULT_WIDTH;
  })[0];
  const aiTutorResize = useResize({
    initialSize: aiTutorInitialWidth,
    minSize: Math.max(AI_TUTOR_MIN_WIDTH, chatPanelMinWidth),
    maxSize: AI_TUTOR_MAX_WIDTH,
    direction: "horizontal",
    invertHorizontal: true,
    onSizeChange: (s) => {
      try {
        localStorage.setItem(AI_TUTOR_ASIDE_WIDTH_KEY, String(s));
      } catch {}
    },
  });

  // Открываем сайдбар модулей по умолчанию на больших экранах
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => setSidebarVisible(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // AI Tutor открыт по умолчанию на desktop
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const stored = localStorage.getItem(`ai-tutor-course-open-${slug}`);
    if (stored !== null) {
      setShowAITutor(mq.matches && stored === "true");
    } else {
      setShowAITutor(mq.matches);
    }
    const handleChange = () => {
      if (!mq.matches) setShowAITutor(false);
    };
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, [slug]);

  useEffect(() => {
    if (slug) localStorage.setItem(`ai-tutor-course-open-${slug}`, String(showAITutor));
  }, [slug, showAITutor]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsLg(mq.matches);
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const toggleSidebar = () => setSidebarVisible((v) => !v);

  const renderStatusDot = (lessonId: string) => {
    const status = progress[lessonId] || "not_started";
    const color =
      status === "done" ? "bg-green-500" : status === "in_progress" ? "bg-orange-400" : "bg-neutral-border";
    return <span className={`w-2.5 h-2.5 rounded-full ${color}`} />;
  };

  // Все уроки для подсчета
  const allLessons = course ? course.modules.flatMap((m) => m.lessons) : [];
  const firstLessonId = useMemo(() => {
    if (!course || course.modules.length === 0) return null;
    const firstModule = course.modules[0];
    return firstModule.lessons[0]?.id || null;
  }, [course]);

  // Находим последний пройденный урок (in_progress или done)
  const lastProgressLessonId = useMemo(() => {
    if (!course) return null;
    
    // Собираем все уроки в порядке следования
    const allLessons = course.modules.flatMap(m => m.lessons);
    
    // Ищем последний урок со статусом in_progress или done
    for (let i = allLessons.length - 1; i >= 0; i--) {
      const lessonId = allLessons[i].id;
      const status = progress[lessonId] || "not_started";
      if (status === "in_progress" || status === "done") {
        return lessonId;
      }
    }
    
    return null;
  }, [course, progress]);

  // Определяем, был ли курс начат
  const isCourseStarted = lastProgressLessonId !== null;
  const continueLessonId = lastProgressLessonId || firstLessonId;

  const handleStartCourse = () => {
    if (!slug || !continueLessonId) return;
    navigate(`/courses/${slug}/${continueLessonId}`);
    const status = progress[continueLessonId] || "not_started";
    if (status === "not_started") setLessonStatus(continueLessonId, "in_progress");
  };

  if (courseLoading) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center bg-neutral-light p-4">
        <div className="bg-white border border-neutral-border rounded-xl p-6 shadow-sm max-w-md w-full">
          <div className="animate-pulse">
            <div className="h-8 bg-neutral-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (courseError || !course) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center bg-neutral-light p-4">
        <div className="bg-white border border-neutral-border rounded-xl p-6 shadow-sm max-w-md w-full">
          <h1 className="text-2xl font-heading font-bold text-neutral-text mb-2">Курс не найден</h1>
          <p className="text-neutral-text/70 mb-4">
            {courseError || "Проверьте адрес или вернитесь к списку курсов."}
          </p>
          <Link
            to="/courses"
            className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
          >
            Назад к курсам
          </Link>
        </div>
      </div>
    );
  }

  const overallProgress = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  const courseTitle = course.title || "Курс";
  const courseDescription = course.description || course.subtitle || "Изучайте ClickHouse на практике";
  const courseUrl = `${SITE_URL}/courses/${slug}`;

  return (
    <div
        className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden bg-neutral-light"
        style={{ paddingRight: isLg && showAITutor ? Math.max(aiTutorResize.size, chatPanelMinWidth) : 0 }}
      >
      <SEOHead
        title={`${courseTitle} — Базовый курс | Quantile Camp`}
        description={courseDescription}
        keywords={`${courseTitle}, ClickHouse курс, SQL обучение, ClickHouse для начинающих, базы данных курс, аналитика данных обучение, Data Engineering, Big Data, ClickHouse tutorial, SQL практика, онлайн курс ClickHouse, интерактивное обучение, ClickHouse основы, структура ClickHouse, SQL запросы ClickHouse`}
        type="course"
        structuredData={[
          generateCourseSchema({
            title: courseTitle,
            description: courseDescription,
            url: courseUrl,
            level: course.level || "Beginner"
          }),
          generateBreadcrumbSchema([
            { name: "Главная", url: "/" },
            { name: "Курсы", url: "/courses" },
            { name: courseTitle, url: `/courses/${slug}` }
          ])
        ]}
      />

      {/* Mobile toolbar */}
      <div className="lg:hidden flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-white border-b border-neutral-border">
        <button
          onClick={toggleSidebar}
          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
            sidebarVisible
              ? "bg-primary/10 text-primary border-2 border-primary/30"
              : "bg-white border-2 border-neutral-border shadow-sm text-neutral-text hover:bg-primary/10 hover:border-primary/30"
          }`}
          aria-label={sidebarVisible ? "Скрыть модули" : "Модули"}
        >
          {sidebarVisible ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}
          <span>Модули</span>
        </button>
        <span className="flex-1 truncate text-sm font-medium text-neutral-text">{course.title}</span>
        <button
          onClick={() => {
            setSidebarVisible(false);
            if (showAITutor && tutorAsideMode === "chat") {
              setShowAITutor(false);
            } else {
              setTutorAsideMode("chat");
              setShowAITutor(true);
            }
          }}
          className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
            showAITutor && tutorAsideMode === "chat"
              ? "bg-primary/15 text-primary border-2 border-primary/40"
              : "bg-white border-2 border-primary/30 shadow-sm text-primary hover:bg-primary/10 hover:border-primary/40"
          }`}
          aria-label={showAITutor && tutorAsideMode === "chat" ? "Скрыть чат" : "AI Tutor — чат"}
        >
          <MessageCircle className="w-5 h-5" />
          <span>Чат</span>
        </button>
      </div>

      {/* Кнопка развернуть модули (когда свёрнуты) */}
      {!sidebarVisible && (
        <button
          onClick={toggleSidebar}
          className="hidden lg:flex fixed left-0 top-[calc(var(--header-height)+0.5rem)] z-30 w-8 h-12 items-center justify-center bg-white border border-neutral-border border-l-0 rounded-r-lg shadow-sm hover:bg-neutral-light transition-colors"
          aria-label="Показать модули"
        >
          <PanelLeftOpen className="w-4 h-4 text-neutral-text/60" />
        </button>
      )}

      {/* Левая колонка: модули (Cursor-like) */}
      <aside
        className={`hidden lg:flex flex-shrink-0 flex-col border-r border-neutral-border bg-white transition-all duration-300 overflow-hidden ${
          sidebarVisible ? "w-[280px]" : "w-0"
        }`}
      >
            <div className="p-4 border-b border-neutral-border flex-shrink-0 flex items-center justify-between gap-2">
              <div>
                <div className="text-sm text-neutral-text/70">Модули курса</div>
                <div className="text-xl font-semibold text-neutral-text">{course.modules.length} модулей</div>
              </div>
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg hover:bg-neutral-light transition-colors"
                aria-label="Скрыть модули"
              >
                <PanelLeftClose className="w-5 h-5 text-neutral-text/60" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              <div className="relative space-y-0">
                {course.modules.map((module, idx) => (
                  <ModuleListItem
                    key={module.id}
                    module={module}
                    progress={progress}
                    index={idx}
                    isLast={idx === course.modules.length - 1}
                    onSelect={() => setSelectedModuleId(module.id)}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoveredModuleId(module.id);
                      setTooltipPosition({
                        top: rect.top + rect.height / 2,
                        left: rect.right + 12,
                      });
                    }}
                    onMouseLeave={() => {
                      setHoveredModuleId(null);
                      setTooltipPosition(null);
                    }}
                  />
                ))}
              </div>
            </div>
            {/* Тултип с fixed positioning (только на десктопе) */}
            {hoveredModuleId && tooltipPosition && (() => {
              const module = course.modules.find(m => m.id === hoveredModuleId);
              if (!module) return null;
              const moduleLessons = module.lessons;
              const completedLessons = moduleLessons.filter(l => progress[l.id] === "done").length;
              const inProgressLessons = moduleLessons.filter(l => progress[l.id] === "in_progress").length;
              const moduleProgress = moduleLessons.length > 0
                ? Math.round((completedLessons / moduleLessons.length) * 100)
                : 0;
              const totalTime = moduleLessons.reduce((sum, l) => sum + (l.estimatedTime || 0), 0);
              const isCompleted = moduleProgress === 100;
              const isInProgress = moduleProgress > 0 && moduleProgress < 100;
              
              return (
                <div
                  className="hidden lg:block fixed z-[100] pointer-events-none"
                  style={{
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    transform: 'translateY(-50%)'
                  }}
                >
                  <div className="w-72 rounded-xl border-2 border-neutral-border bg-white shadow-2xl overflow-hidden">
                    {/* Заголовок с цветовой индикацией */}
                    <div className={`p-3 ${
                      isCompleted ? 'bg-primary/10' : isInProgress ? 'bg-orange-400/10' : 'bg-neutral-100'
                    }`}>
                      <div>
                        <h4 className="font-semibold text-sm text-neutral-text leading-tight">
                          {module.title}
                        </h4>
                      </div>
                    </div>
                    
                    {/* Контент */}
                    <div className="p-3 space-y-2.5">
                      {module.description && (
                        <p className="text-xs text-neutral-text/70 line-clamp-2 leading-relaxed">
                          {module.description}
                        </p>
                      )}
                      
                      {/* Статистика */}
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-neutral-border/50">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                            isCompleted ? 'bg-primary/10' : 'bg-neutral-100'
                          }`}>
                            <CheckCircle2 className={`w-3.5 h-3.5 ${
                              isCompleted ? 'text-primary' : 'text-neutral-text/50'
                            }`} />
                          </div>
                          <div>
                            <div className="text-[10px] text-neutral-text/50 leading-none mb-0.5">Завершено</div>
                            <div className="text-xs font-semibold text-neutral-text">{completedLessons}/{moduleLessons.length}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1.5">
                          <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                            inProgressLessons > 0 ? 'bg-orange-400/10' : 'bg-neutral-100'
                          }`}>
                            <PlayCircle className={`w-3.5 h-3.5 ${
                              inProgressLessons > 0 ? 'text-orange-400' : 'text-neutral-text/50'
                            }`} />
                          </div>
                          <div>
                            <div className="text-[10px] text-neutral-text/50 leading-none mb-0.5">В процессе</div>
                            <div className="text-xs font-semibold text-neutral-text">{inProgressLessons}</div>
                          </div>
                        </div>
                      </div>
                      
                      {totalTime > 0 && (
                        <div className="flex items-center gap-1.5 text-xs text-neutral-text/60 pt-1">
                          <Clock className="w-3.5 h-3.5" />
                          <span>Примерно {totalTime} мин</span>
                        </div>
                      )}
                      
                      {/* Прогресс-бар */}
                      <div className="pt-2">
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              isCompleted ? 'bg-primary' : isInProgress ? 'bg-orange-400' : 'bg-neutral-300'
                            }`}
                            style={{ width: `${moduleProgress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </aside>

      {/* Центр: контент курса (скролл) */}
      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden bg-neutral-light">
        <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 w-full space-y-6">
          <div className="mb-4 flex items-center gap-2 text-sm text-neutral-text/60">
            <Link to="/courses" className="hover:text-primary transition">Курсы</Link>
            <span>·</span>
            <span className="text-neutral-text/80">{course.title}</span>
          </div>
          <div className="space-y-6">
            {/* Хиро-блок (лендинг-знакомство с курсом) */}
            <div className="bg-white border border-neutral-border rounded-xl shadow-sm p-4 sm:p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-2">
                    <Sparkles className="w-4 h-4" />
                    Добро пожаловать в курс
        </div>
                  <h1 className="text-2xl sm:text-3xl font-heading font-bold text-neutral-text mb-2">
                    {course.title}
                  </h1>
                  {course.subtitle && (
                    <p className="text-lg text-neutral-text/80 mb-2">{course.subtitle}</p>
                  )}
                  {course.description && (
                    <p className="text-sm text-neutral-text/70 mb-3">{course.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-sm text-neutral-text/70">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {course.modules.length} модулей
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {allLessons.length} уроков
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" />
                      {stats.done}/{stats.total} завершено
                    </span>
                    <div className="ml-auto flex-shrink-0">
                      {user ? (
                        <button
                          onClick={handleStartCourse}
                          disabled={!continueLessonId}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg shadow-sm hover:bg-primary-dark transition disabled:opacity-50"
                        >
                          <PlayCircle className="w-5 h-5" />
                          {continueLessonId
                            ? (isCourseStarted ? "Продолжить" : "Начать")
                            : "Нет доступных уроков"}
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate("/login")}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg shadow-sm hover:bg-primary-dark transition"
                        >
                          <PlayCircle className="w-5 h-5" />
                          Войти для начала курса
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Статистика курса */}
              <div className="space-y-3 pt-4 border-t border-neutral-border mt-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-text/70">Общий прогресс</span>
                  <span className="text-sm font-bold text-neutral-text">{overallProgress}%</span>
                </div>
                <div className="h-2 bg-neutral-light rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${overallProgress}%` }}
              />
          </div>
                <div className="flex justify-between text-sm text-neutral-text/60">
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>{stats.done} из {stats.total} уроков завершено</span>
                  </div>
                </div>
          </div>
      </div>

            {/* Требования и необходимые знания */}
            {(course.requirements || course.prerequisites) && (
              <div className="bg-white border border-neutral-border rounded-xl shadow-sm p-4 sm:p-6">
                <h3 className="text-lg font-heading font-bold text-neutral-text mb-4">
                  Требования и необходимые знания
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {course.requirements && course.requirements.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-text mb-2 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        Требования к курсу
                      </h4>
                      <ul className="space-y-1.5">
                        {course.requirements.map((req, idx) => (
                          <li key={idx} className="text-sm text-neutral-text/70 flex items-start gap-2">
                            <span className="text-primary mt-1">•</span>
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {course.prerequisites && course.prerequisites.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-text mb-2 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-primary" />
                        Необходимые знания
                      </h4>
                      <ul className="space-y-1.5">
                        {course.prerequisites.map((prereq, idx) => (
                          <li key={idx} className="text-sm text-neutral-text/70 flex items-start gap-2">
                            <span className="text-primary mt-1">•</span>
                            <span>{prereq}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Быстрый старт */}
            <div className="bg-white border border-neutral-border rounded-xl shadow-sm p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-heading font-bold text-neutral-text">
                  Быстрый старт
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-neutral-text mb-1">Начните с первого урока</h4>
                    <p className="text-sm text-neutral-text/70">
                      Нажмите кнопку "Начать первый урок" выше или выберите модуль в левом сайдбаре, чтобы открыть список уроков.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-neutral-text mb-1">Изучайте последовательно</h4>
                    <p className="text-sm text-neutral-text/70">
                      Уроки в каждом модуле связаны между собой. Рекомендуем проходить их по порядку для лучшего понимания материала.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-neutral-text mb-1">Практикуйтесь в тренажере</h4>
                    <p className="text-sm text-neutral-text/70">
                      После каждого урока закрепляйте знания, выполняя практические задания в интерактивном тренажере SQL.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-neutral-text mb-1">Отслеживайте прогресс</h4>
                    <p className="text-sm text-neutral-text/70">
                      Ваш прогресс автоматически сохраняется. Вы всегда можете вернуться к любому уроку и продолжить с того места, где остановились.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Советы для успешного прохождения */}
            <div className="bg-white border border-neutral-border rounded-xl shadow-sm p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-orange-400/10 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-orange-400" />
                </div>
                <h3 className="text-lg font-heading font-bold text-neutral-text">
                  Советы для успешного прохождения
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-neutral-light/50">
                  <Target className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-neutral-text mb-1">Ставьте цели</h4>
                    <p className="text-xs text-neutral-text/70">
                      Определите, сколько уроков вы хотите пройти за день или неделю. Это поможет сохранить мотивацию.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-neutral-light/50">
                  <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-neutral-text mb-1">Выделяйте время регулярно</h4>
                    <p className="text-xs text-neutral-text/70">
                      Лучше заниматься по 30-60 минут каждый день, чем пытаться пройти весь курс за один раз.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-neutral-light/50">
                  <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-neutral-text mb-1">Практикуйтесь активно</h4>
                    <p className="text-xs text-neutral-text/70">
                      Не просто читайте материал — обязательно выполняйте все практические задания и экспериментируйте с кодом.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-neutral-light/50">
                  <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-neutral-text mb-1">Повторяйте сложные темы</h4>
                    <p className="text-xs text-neutral-text/70">
                      Если что-то непонятно, вернитесь к уроку позже. Повторение — ключ к закреплению знаний.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-neutral-light/50">
                  <BookOpen className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-neutral-text mb-1">Делайте заметки</h4>
                    <p className="text-xs text-neutral-text/70">
                      Записывайте важные концепции и примеры запросов. Это поможет быстро вспомнить материал в будущем.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-neutral-light/50">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-neutral-text mb-1">Не бойтесь ошибок</h4>
                    <p className="text-xs text-neutral-text/70">
                      Ошибки — это часть обучения. Анализируйте их, исправляйте и продолжайте двигаться вперед.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Кнопки развернуть AI Tutor / Roadmap (когда свёрнут) */}
      {!showAITutor && (
        <div className="hidden lg:flex fixed right-0 top-[calc(var(--header-height)+0.5rem)] z-30 flex-col gap-1">
          <button
            type="button"
            onClick={() => {
              setTutorAsideMode("chat");
              setShowAITutor(true);
            }}
            className="flex h-10 w-10 items-center justify-center bg-white border border-neutral-border border-r-0 rounded-l-lg shadow-sm hover:bg-primary/5 hover:border-primary/40 transition-colors"
            aria-label="Показать AI Tutor"
            title="Чат"
          >
            <MessageCircle className="w-4 h-4 text-primary" />
          </button>
          <button
            type="button"
            onClick={() => {
              setTutorAsideMode("roadmap");
              setShowAITutor(true);
            }}
            className="flex h-10 w-10 items-center justify-center bg-white border border-neutral-border border-r-0 rounded-l-lg shadow-sm hover:bg-primary/5 hover:border-primary/40 transition-colors"
            aria-label="Показать Roadmap"
            title="Roadmap"
          >
            <Map className="w-4 h-4 text-primary" />
          </button>
        </div>
      )}

      {/* Ресайзер AI Tutor (fixed, у левого края панели) */}
      {showAITutor && (
        <div
          onMouseDown={aiTutorResize.onMouseDown}
          className="hidden lg:flex fixed z-[56] justify-center cursor-col-resize"
          style={{
            width: "8px",
            top: AI_TUTOR_DESKTOP_TOP,
            bottom: 0,
            right: Math.max(aiTutorResize.size, chatPanelMinWidth) - 4,
          }}
        >
          <div className="w-1 rounded bg-transparent hover:bg-primary/40 transition-colors" style={{ height: "100%" }} />
        </div>
      )}

      {/* Правая колонка: AI Tutor — fixed, перекрывает хедер (full height) */}
      {showAITutor && (
        <aside
          ref={aiTutorResize.elementRef}
          className="hidden lg:flex flex-col min-h-0 border-l border-neutral-border bg-white overflow-hidden"
          style={{
            position: "fixed",
            top: AI_TUTOR_DESKTOP_TOP,
            right: 0,
            bottom: 0,
            width: Math.max(aiTutorResize.size, chatPanelMinWidth),
            zIndex: LAYERS.aiTutorPanel,
          }}
        >
          <AITutorCoursePanel
            user={user}
            courseSlug={slug || ""}
            courseTitle={courseTitle}
            modules={course.modules}
            onClose={() => setShowAITutor(false)}
            embedded
            onMinWidthChange={setChatPanelMinWidth}
            asideMode={tutorAsideMode}
            onAsideModeChange={setTutorAsideMode}
          />
        </aside>
      )}

      {/* Mobile: AI Tutor fullscreen overlay */}
      {showAITutor && (
        <div
          className="lg:hidden fixed inset-x-0 top-[calc(var(--header-height,4.5rem)+3rem)] bottom-0 flex flex-col bg-white border-t border-neutral-border"
          style={{ zIndex: LAYERS.mobileOverlay }}
        >
          <AITutorCoursePanel
            user={user}
            courseSlug={slug || ""}
            courseTitle={courseTitle}
            modules={course.modules}
            onClose={() => setShowAITutor(false)}
            embedded
            asideMode={tutorAsideMode}
            onAsideModeChange={setTutorAsideMode}
          />
        </div>
      )}

      {/* Mobile: модули drawer */}
      {sidebarVisible && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50"
          style={{ zIndex: LAYERS.mobileBackdrop }}
          onClick={() => setSidebarVisible(false)}
          aria-hidden="true"
        />
      )}
      {sidebarVisible && (
        <aside
          className="lg:hidden fixed left-0 top-0 bottom-0 w-[280px] max-w-[85vw] bg-white border-r border-neutral-border shadow-xl overflow-y-auto"
          style={{ zIndex: LAYERS.mobileDrawer }}
        >
          <div className="p-4 border-b border-neutral-border flex items-center justify-between">
            <span className="font-semibold text-neutral-text">Модули курса</span>
            <button onClick={() => setSidebarVisible(false)} className="p-2 hover:bg-neutral-light rounded-lg" aria-label="Закрыть">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 space-y-0">
            {course.modules.map((module, idx) => (
              <ModuleListItem
                key={module.id}
                module={module}
                progress={progress}
                index={idx}
                isLast={idx === course.modules.length - 1}
                onSelect={() => {
                  setSelectedModuleId(module.id);
                  setSidebarVisible(false);
                }}
                compact
              />
            ))}
          </div>
        </aside>
      )}

      {/* Модальное окно с уроками модуля */}
      {selectedModuleId && (() => {
        const module = course.modules.find(m => m.id === selectedModuleId);
        if (!module) return null;
        
        const moduleLessons = module.lessons;
        const completedLessons = moduleLessons.filter(l => progress[l.id] === "done").length;
        const moduleProgress = moduleLessons.length > 0
          ? Math.round((completedLessons / moduleLessons.length) * 100)
          : 0;
        const totalTime = moduleLessons.reduce((sum, l) => sum + (l.estimatedTime || 0), 0);
        
        return (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/50 p-2 sm:p-4"
            style={{ zIndex: LAYERS.modal }}
            onClick={() => setSelectedModuleId(null)}
          >
            <div 
              className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Заголовок модального окна */}
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 sm:p-6 border-b border-neutral-border">
                <div className="flex items-start justify-between gap-2 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                      </div>
                      <h2 className="text-lg sm:text-2xl font-heading font-bold text-neutral-text leading-snug break-words">
                        {module.title}
                      </h2>
                    </div>
                    {module.description && (
                      <p className="text-xs sm:text-sm text-neutral-text/70 mb-2 sm:mb-3 line-clamp-2">
                        {module.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-neutral-text/60">
                      <span className="flex items-center gap-1.5">
                        <BookOpen className="w-4 h-4" />
                        {moduleLessons.length} уроков
                      </span>
                      {totalTime > 0 && (
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />
                          ~{totalTime} мин
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4" />
                        {moduleProgress}% завершено
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedModuleId(null)}
                    className="p-2 hover:bg-neutral-100 rounded-lg transition"
                    aria-label="Закрыть"
                  >
                    <X className="w-5 h-5 text-neutral-text/60" />
                  </button>
                </div>
                
                {/* Прогресс-бар */}
                <div className="mt-4 relative">
                  <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 relative ${
                        moduleProgress === 100 ? 'bg-primary' : moduleProgress > 0 ? 'bg-orange-400' : 'bg-neutral-300'
                      }`}
                      style={{ width: `${moduleProgress}%` }}
                    >
                      {moduleProgress === 100 && (
                        <span className="absolute inset-0 flex items-center justify-end pr-1 text-[10px] font-semibold text-white pointer-events-none">
                          100%
                        </span>
                      )}
                    </div>
                  </div>
                  {moduleProgress !== 100 && (
                    <span className="absolute -top-0.5 right-0 text-[10px] font-semibold text-neutral-text/70">
                      {moduleProgress}%
                    </span>
                  )}
                </div>
              </div>

              {/* Список уроков */}
              <div className="overflow-y-auto max-h-[calc(95vh-220px)] sm:max-h-[calc(90vh-240px)] p-3 sm:p-6">
                <div className="space-y-2">
                  {moduleLessons.map((lesson, idx) => {
                    const status = progress[lesson.id] || "not_started";
                    const isCompleted = status === "done";
                    const isInProgress = status === "in_progress";
                    
                    return (
                      <button
                        key={lesson.id}
                        onClick={() => {
                          navigate(`/courses/${slug}/${lesson.id}`);
                          setSelectedModuleId(null);
                        }}
                        className="w-full flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-neutral-border hover:border-primary/50 hover:bg-primary/5 transition-all group"
                      >
                        {/* Номер и статус */}
                        <div className="flex flex-col items-center gap-2 pt-0.5 sm:pt-1 flex-shrink-0">
                          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center font-semibold text-xs sm:text-sm transition-all ${
                            isCompleted
                              ? 'bg-primary text-white'
                              : isInProgress
                              ? 'bg-orange-400 text-white'
                              : 'bg-neutral-100 text-neutral-text/50 group-hover:bg-primary/10 group-hover:text-primary'
                          }`}>
                            {isCompleted ? (
                              <CheckCircle2 className="w-5 h-5" />
                            ) : (
                              <span>{idx + 1}</span>
                            )}
                          </div>
                        </div>

                        {/* Информация об уроке */}
                        <div className="flex-1 text-left min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-neutral-text mb-1 group-hover:text-primary transition-colors">
                            {lesson.title}
                          </h3>
                          {lesson.summary && (
                            <p className="text-xs sm:text-sm text-neutral-text/60 mb-1.5 sm:mb-2 line-clamp-2">
                              {lesson.summary}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-neutral-text/50">
                            {lesson.estimatedTime && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {lesson.estimatedTime} мин
                              </span>
                            )}
                            {isCompleted && (
                              <span className="flex items-center gap-1 text-primary">
                                <CheckCircle2 className="w-3 h-3" />
                                Завершено
                              </span>
                            )}
                            {isInProgress && (
                              <span className="flex items-center gap-1 text-orange-400">
                                <PlayCircle className="w-3 h-3" />
                                В процессе
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Стрелка */}
                        <div className="pt-0.5 sm:pt-1 hidden sm:block flex-shrink-0">
                          <ArrowRight className="w-5 h-5 text-neutral-text/30 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
